import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import './AdminPanel.css';

export const AdminPanel = () => {
  const { getAccessToken } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [targetEmail, setTargetEmail] = useState('');
  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [allowedEnvs, setAllowedEnvs] = useState(['prod']);
  const [queryEmail, setQueryEmail] = useState('');
  const [userScripts, setUserScripts] = useState([]);

  useEffect(() => {
    loadAllScripts();
  }, []);

  const loadAllScripts = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      apiClient.setAuthToken(token);
      const data = await apiClient.getScripts();
      setScripts(data.scripts || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const queryUserScripts = async (e) => {
    e.preventDefault();
    if (!queryEmail) return;

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      apiClient.setAuthToken(token);
      const data = await apiClient.getScripts(queryEmail);
      setUserScripts(data.scripts || []);
      setSuccess(`Loaded scripts for ${queryEmail}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setUserScripts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEnablement = async (e) => {
    e.preventDefault();

    if (!targetEmail || !selectedScriptId) {
      setError('Email and script are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const token = await getAccessToken();
      apiClient.setAuthToken(token);

      await apiClient.updateEnablement({
        targetEmail,
        scriptId: selectedScriptId,
        enabled,
        allowedEnvs: allowedEnvs.length > 0 ? allowedEnvs : undefined
      });

      setSuccess(`Successfully ${enabled ? 'enabled' : 'disabled'} ${selectedScriptId} for ${targetEmail}`);
      
      // Reset form
      setTargetEmail('');
      setSelectedScriptId('');
      setEnabled(true);
      setAllowedEnvs(['prod']);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEnv = (env) => {
    setAllowedEnvs(prev => 
      prev.includes(env) 
        ? prev.filter(e => e !== env)
        : [...prev, env]
    );
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      <p className="admin-subtitle">Manage user script access and query enablements</p>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      <div className="admin-grid">
        {/* Query User Scripts */}
        <div className="admin-card">
          <h3>Query User Scripts</h3>
          <form onSubmit={queryUserScripts}>
            <div className="form-group">
              <label>User Email</label>
              <input
                type="email"
                value={queryEmail}
                onChange={(e) => setQueryEmail(e.target.value)}
                placeholder="user@example.com"
                className="form-control"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Query Scripts'}
            </button>
          </form>

          {userScripts.length > 0 && (
            <div className="user-scripts-result">
              <h4>Scripts for {queryEmail}</h4>
              <div className="scripts-list">
                {userScripts.map(script => (
                  <div key={script.scriptId} className="script-item">
                    <div className="script-item-header">
                      <strong>{script.displayName}</strong>
                      <span className={`risk-badge risk-${script.riskTier}`}>
                        {script.riskTier}
                      </span>
                    </div>
                    <div className="env-badges">
                      {script.allowedEnvs.map(env => (
                        <span key={env} className="env-badge">{env}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Manage Enablements */}
        <div className="admin-card">
          <h3>Manage Script Access</h3>
          <form onSubmit={handleUpdateEnablement}>
            <div className="form-group">
              <label>Target User Email</label>
              <input
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                placeholder="user@example.com"
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label>Script</label>
              <select
                value={selectedScriptId}
                onChange={(e) => setSelectedScriptId(e.target.value)}
                className="form-control"
                required
              >
                <option value="">Select a script...</option>
                {scripts.map(script => (
                  <option key={script.scriptId} value={script.scriptId}>
                    {script.displayName} ({script.scriptId})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Action</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    checked={enabled}
                    onChange={() => setEnabled(true)}
                  />
                  Enable
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    checked={!enabled}
                    onChange={() => setEnabled(false)}
                  />
                  Disable
                </label>
              </div>
            </div>

            {enabled && (
              <div className="form-group">
                <label>Allowed Environments (optional)</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={allowedEnvs.includes('prod')}
                      onChange={() => toggleEnv('prod')}
                    />
                    Production
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={allowedEnvs.includes('dev')}
                      onChange={() => toggleEnv('dev')}
                    />
                    Development
                  </label>
                </div>
                <small>Leave unchecked to inherit from script defaults</small>
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Access'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
